import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { useSearchSuggestions } from '../hooks/useSearchSuggestions';
import { KIND_LABELS } from '../lib/searchNav';

const normalize = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const countsLabel = (s) => {
    const parts = [];
    if (s.bf_count > 0) parts.push(`${s.bf_count} offre${s.bf_count > 1 ? 's' : ''} BF`);
    if (s.plus_count > 0) parts.push(`${s.plus_count} Offres+`);
    return parts.join(' · ');
};

/**
 * Barre de recherche avec suggestions (pays, villes, entreprises, secteurs).
 * - Entrée sur une suggestion (flèches + Entrée, ou clic) → onPick(suggestion)
 * - Entrée sans suggestion choisie → onSubmit(texte), sauf si le texte est exactement un pays
 */
const SearchBox = ({
    initialValue = '',
    placeholder = 'Rechercher un pays, une entreprise, un intitulé…',
    onSubmit,
    onPick,
    wrapperClassName = '',
    inputClassName = '',
    iconClassName = 'text-slate-400',
    showIcon = true,
    autoFocus = false,
}) => {
    const [value, setValue] = useState(initialValue);
    const [focused, setFocused] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const inputRef = useRef(null);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    const { suggestions, loading } = useSearchSuggestions(focused ? value : '');
    const trimmed = value.trim();
    const open = focused && trimmed.length >= 2;

    const grouped = useMemo(() => {
        const groups = {};
        suggestions.forEach((s) => {
            (groups[s.kind] = groups[s.kind] || []).push(s);
        });
        return Object.entries(groups);
    }, [suggestions]);

    const flat = useMemo(() => grouped.flatMap(([, items]) => items), [grouped]);

    useEffect(() => {
        setActiveIndex(-1);
    }, [suggestions]);

    const pick = (s) => {
        setFocused(false);
        inputRef.current?.blur();
        onPick?.(s);
    };

    const submitText = () => {
        if (!trimmed) return;
        const exactCountry = flat.find((s) => s.kind === 'pays' && normalize(s.label) === normalize(trimmed));
        if (exactCountry) {
            pick(exactCountry);
            return;
        }
        setFocused(false);
        inputRef.current?.blur();
        onSubmit?.(trimmed);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, flat.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, -1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (activeIndex >= 0 && flat[activeIndex]) pick(flat[activeIndex]);
            else submitText();
        } else if (e.key === 'Escape') {
            setFocused(false);
            inputRef.current?.blur();
        }
    };

    let rowIndex = -1;

    return (
        <div className={`relative ${wrapperClassName}`}>
            {showIcon && (
                <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${iconClassName}`} />
            )}
            <input
                ref={inputRef}
                type="search"
                autoComplete="off"
                autoFocus={autoFocus}
                placeholder={placeholder}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onKeyDown={handleKeyDown}
                className={inputClassName}
                role="combobox"
                aria-expanded={open}
                aria-autocomplete="list"
            />

            {open && (
                <div
                    className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-slate-200 z-[60] overflow-hidden text-left"
                    onMouseDown={(e) => e.preventDefault()}
                    role="listbox"
                >
                    {loading && flat.length === 0 && (
                        <div className="flex items-center gap-2 px-4 py-3 text-sm text-slate-400">
                            <Loader2 className="w-4 h-4 animate-spin" /> Recherche…
                        </div>
                    )}

                    {grouped.map(([kind, items]) => (
                        <div key={kind} className="py-1">
                            <p className="px-4 pt-2 pb-1 text-[10px] font-black uppercase tracking-wider text-slate-400">
                                {KIND_LABELS[kind]}
                            </p>
                            {items.map((s) => {
                                rowIndex += 1;
                                const idx = rowIndex;
                                return (
                                    <button
                                        key={`${kind}-${s.label}`}
                                        type="button"
                                        role="option"
                                        aria-selected={activeIndex === idx}
                                        onClick={() => pick(s)}
                                        onMouseEnter={() => setActiveIndex(idx)}
                                        className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left transition-colors ${activeIndex === idx ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                                    >
                                        <span className="text-sm font-semibold text-slate-800 truncate">{s.label}</span>
                                        <span className="text-xs text-slate-400 whitespace-nowrap">{countsLabel(s)}</span>
                                    </button>
                                );
                            })}
                        </div>
                    ))}

                    {trimmed.length >= 2 && (
                        <button
                            type="button"
                            onClick={submitText}
                            className="w-full flex items-center gap-2 px-4 py-3 border-t border-slate-100 text-sm font-bold text-blue-600 hover:bg-blue-50 transition-colors"
                        >
                            <Search className="w-4 h-4" />
                            Rechercher « {trimmed} » dans toutes les offres
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchBox;
